import type { Profile } from '../../types/profile';

export type DemoStatus = 'active' | 'expiring_soon' | 'expired' | 'converted' | 'cancelled';

export function isDemoUser(profile?: Profile | null): boolean {
  if (!profile) return false;
  return Boolean(profile.is_demo || profile.demo_role);
}

export function getDemoStatus(profile?: Profile | null): DemoStatus | null {
  if (!profile || !isDemoUser(profile)) return null;
  return (profile.demo_status as DemoStatus | null) ?? null;
}

export function getDemoRemainingDays(profile?: Profile | null, now: Date = new Date()): number | null {
  if (!profile?.demo_expires_at) return null;

  const expiresAt = new Date(profile.demo_expires_at);
  if (Number.isNaN(expiresAt.getTime())) return null;

  const diffMs = expiresAt.getTime() - now.getTime();
  if (diffMs <= 0) return 0;

  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

export function isDemoExpired(profile?: Profile | null, now: Date = new Date()): boolean {
  if (!isDemoUser(profile)) return false;
  const status = getDemoStatus(profile);
  if (status === 'expired' || status === 'cancelled') return true;
  return getDemoRemainingDays(profile, now) === 0;
}

export function isDemoReadonly(profile?: Profile | null, now: Date = new Date()): boolean {
  if (!isDemoUser(profile)) return false;
  return profile?.demo_role === 'demo_readonly' || isDemoExpired(profile, now);
}

export function formatDemoDate(dateStr?: string | null): string | null {
  if (!dateStr) return null;
  const value = new Date(dateStr);
  if (Number.isNaN(value.getTime())) return null;

  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(value);
}
